/**
 * Collections over MCP — the CREATE half. `spec_import` lives beside this in
 * `collectionImport.js`, because it fails differently (a parse error is the
 * common case) and because it is the one tool here that writes many records.
 *
 * ## The rules, each of which prevents a specific verified loss
 *
 * 1. **Everything goes through `applyCollectionsChange`**, which routes the
 *    write through `setCollections`. A write that reached storage any other
 *    way is REVERTED by the user's next keystroke — see `collectionsDb.js`'s
 *    header, which is the single most important comment in this feature.
 *
 * 2. **`next` is derived from the array handed in by a single-record change.**
 *    Never rebuilt from anything else. The persist that follows tombstones
 *    every collection and request absent from that array, on every device,
 *    with no undo. `assertNoCollectionLost` refuses the write if a change
 *    function ever gets this wrong.
 *
 * 3. **Records are built by `collectionRecords.js` through the app's own pure
 *    helpers**, never field-by-field here. A field left out of a record does
 *    not default — it stops existing on the next whole-snapshot save.
 *
 * 4. **A literal credential is refused, in the auth block and in the url**
 *    (**O32**). Checked before anything is read.
 *
 * ## What these tools cannot do, and it is structural rather than pending
 *
 * They cannot send a request, read a response, read a variable's value, write
 * a script (**O33**), start the mock server (**O31**), or touch a workspace
 * other than the open one (**O27**). Tranche 1 also cannot UPDATE or DELETE
 * anything, which is why no tool here takes an `expected_rev`: there is
 * nothing to conflict with.
 */
import { mcpError } from '../errors';
import {
  addFolder,
  buildCollection,
  buildRequest,
  checkAuth,
  checkBody,
  checkKvRows,
  checkMethod,
  checkText,
  checkUrl,
  collectionNotFound,
  nextSortOrder,
  pathOf,
} from '../collectionRecords';
import { applyCollectionsChange, findCollection } from '../collectionsDb';
import { getFolderById, normalizeParentId } from '../../utils/collectionFolders';
import { templateRefNames } from '../templateRefs';

/** Every create answers in the same shape, so a client never special-cases. */
function created(kind, fields) {
  return { created: kind, ...fields, scopesConsulted: [], scopesUnavailable: [] };
}

/**
 * The one sentence every create carries. **O36: report, never navigate.** The
 * sidebar's expansion state is component `useState`, so this surface can
 * neither expand a node nor honestly claim the user can see it — and yanking
 * their window to another panel is a UI action nobody asked for.
 */
const FIND_IT_NOTE =
  'A collection or folder in the sidebar may be collapsed, so this may not be visible until the user expands it — tell them the collection and folder by name.';

export async function collectionCreate(args = {}) {
  const name = typeof args.name === 'string' ? args.name.trim() : '';
  if (!name) return mcpError('invalid_argument', '`name` is required and must not be empty.');
  const badText = checkText(args.name, 'name', 200) || checkText(args.description, 'description');
  if (badText) return badText;

  return applyCollectionsChange((collections) => {
    const collection = buildCollection({ name, description: args.description });
    return {
      // Append. Never rebuild — rule 2.
      next: [...collections, collection],
      sizedRecords: [collection],
      result: created('collection', {
        collectionId: collection.id,
        name: collection.name,
        description: collection.description,
        requestCount: 0,
        note: FIND_IT_NOTE,
      }),
    };
  });
}

export async function folderCreate(args = {}) {
  const name = typeof args.name === 'string' ? args.name.trim() : '';
  if (!name) return mcpError('invalid_argument', '`name` is required and must not be empty.');
  const badText = checkText(args.name, 'name', 200) || checkText(args.description, 'description');
  if (badText) return badText;

  return applyCollectionsChange((collections) => {
    const collection = findCollection(collections, args.collection_id);
    if (!collection) return { error: collectionNotFound(args.collection_id) };

    const parentId = normalizeParentId(args.parent_folder_id);
    if (parentId && !getFolderById(collection, parentId)) {
      // `invalid_parent`, not `not_found`: the model's correction is different
      // — re-read the collection, do not create the folder somewhere else.
      return {
        error: mcpError(
          'invalid_parent',
          'That parent folder is not in this collection. Nothing was written. A folder can only nest inside a folder of the SAME collection.',
          { collectionId: String(collection.id), parentFolderId: parentId }
        ),
      };
    }

    const { next: nextCollection, folder } = addFolder(collection, {
      name,
      parentFolderId: parentId,
      description: args.description,
    });
    if (!folder) {
      return { error: mcpError('write_failed', 'The folder could not be created. Nothing was written.') };
    }

    return {
      next: collections.map((c) => (String(c.id) === String(collection.id) ? nextCollection : c)),
      sizedRecords: [nextCollection],
      result: created('folder', {
        collectionId: String(collection.id),
        folderId: String(folder.id),
        name: folder.name,
        description: typeof folder.description === 'string' ? folder.description : '',
        parentFolderId: normalizeParentId(folder.parent_id),
        path: pathOf(nextCollection, folder.id),
        note: FIND_IT_NOTE,
      }),
    };
  });
}

export async function requestCreate(args = {}) {
  // Argument checks first, in a fixed order, so a call with two problems
  // always reports the same one — a refusal that moves around is a refusal a
  // model cannot learn from.
  const refusal =
    checkMethod(args.method) ||
    checkUrl(args.url) ||
    checkText(args.name, 'name', 300) ||
    checkText(args.description, 'description') ||
    checkKvRows(args.headers, 'headers') ||
    checkKvRows(args.params, 'params') ||
    checkBody(args.body) ||
    checkAuth(args.auth);
  if (refusal) return refusal;

  return applyCollectionsChange((collections) => {
    const collection = findCollection(collections, args.collection_id);
    if (!collection) return { error: collectionNotFound(args.collection_id) };

    const folderId = normalizeParentId(args.folder_id);
    if (folderId && !getFolderById(collection, folderId)) {
      return {
        error: mcpError(
          'invalid_parent',
          'That folder is not in this collection. Nothing was written. A request can only live in a folder of the SAME collection.',
          { collectionId: String(collection.id), folderId }
        ),
      };
    }

    const request = buildRequest(
      { ...args, folderId },
      { sortOrder: nextSortOrder(collection, folderId) }
    );
    const nextCollection = {
      ...collection,
      requests: [...(collection.requests || []), request],
    };

    return {
      next: collections.map((c) => (String(c.id) === String(collection.id) ? nextCollection : c)),
      sizedRecords: [request],
      result: created('request', {
        collectionId: String(collection.id),
        requestId: request.id,
        name: request.name,
        method: request.method,
        description: request.description,
        folderId,
        path: pathOf(nextCollection, folderId),
        // The variables this request is WIRED to, by NAME. Never a value
        // (**O7**) — this server cannot read one and does not try. It is the
        // actionable half: the agent can tell the user exactly which variables
        // to fill in before the request will work.
        wiredTo: wiredVariableNames(request),
        note: FIND_IT_NOTE,
      }),
    };
  });
}

/**
 * Every `{{name}}` the request references, across url, header and param values
 * and the auth block. NAMES ONLY, and they came from the client's own
 * arguments a moment ago — nothing is read out of the user's workspace.
 */
function wiredVariableNames(request) {
  const sources = [
    request.url,
    ...(request.headers || []).flatMap((h) => [h.key, h.value]),
    ...(request.params || []).flatMap((p) => [p.key, p.value]),
    request.auth?.bearer,
    request.auth?.apikey?.value,
    request.auth?.basic?.password,
  ];
  const out = [];
  for (const src of sources) {
    for (const name of templateRefNames(src)) {
      if (!out.includes(name)) out.push(name);
    }
  }
  return out;
}
